import React, { createContext, useContext, useState, useEffect } from 'react';
import storage from '../utils/storage';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(true);

  // Available languages
  const availableLanguages = {
    en: 'English',
    fr: 'Français'
  };

  // Detect device language using browser API
  const getDeviceLanguage = () => {
    try {
      // Get browser language
      const browserLang = navigator.language || navigator.userLanguage;
      
      // Extract language code (e.g., 'fr-FR' -> 'fr', 'fr-CA' -> 'fr')
      const languageCode = browserLang.split('-')[0].split('_')[0].toLowerCase();
      
      // Check if we support this language
      if (availableLanguages[languageCode]) {
        return languageCode;
      }
      
      // Return English as fallback
      return 'en';
    } catch (error) {
      return 'en';
    }
  };

  // Language detection and initialization
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        // Check if user has previously selected a language
        const savedLanguage = await storage.getItem('selectedLanguage');
        
        if (savedLanguage && availableLanguages[savedLanguage]) {
          // Use saved language preference
          setLanguage(savedLanguage);
        } else {
          // Default to English for all users
          // Users can change language in settings if desired
          setLanguage('en');
          
          // Save English as the default preference
          await storage.setItem('selectedLanguage', 'en');
        }
      } catch (error) {
        setLanguage('en'); // Fallback to English
      } finally {
        setIsLoading(false);
      }
    };

    initializeLanguage();
  }, []);

  // Change language function
  const changeLanguage = async (newLanguage) => {
    if (availableLanguages[newLanguage]) {
      try {
        await storage.setItem('selectedLanguage', newLanguage);
        setLanguage(newLanguage);
      } catch (error) {
        // Silently handle error
      }
    }
  };

  // Reset to device language
  const resetToDeviceLanguage = async () => {
    try {
      const deviceLanguage = getDeviceLanguage();
      await storage.setItem('selectedLanguage', deviceLanguage);
      setLanguage(deviceLanguage);
    } catch (error) {
      // Silently handle error
    }
  };

  // Get current language name
  const getCurrentLanguageName = () => {
    return availableLanguages[language] || 'English';
  };

  const value = {
    language,
    changeLanguage,
    resetToDeviceLanguage,
    availableLanguages,
    getCurrentLanguageName,
    isLoading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};